import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from 'react-bootstrap/Alert';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import { uniq } from 'lodash';
import Col from 'react-bootstrap/Col';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Card from 'react-bootstrap/Card';
import { Cell, Legend, Pie, PieChart } from 'recharts';
import './DashboardSection.scss';
import Section from './Section';
import { useRouter } from '../util/router';
import { useAuth } from '../util/auth';
import {
  useGetCompanyType,
  getIndexValues,
  useGetRvpCheckData,
  useGetSetupGroups,
  useGetSummaries,
  useGetAllData,
  useGetOmittedData,
  useGetNotIncludeOmittedData
} from '../util/db';
import CustomTable from './Table/CustomTable';
import SQCPieChart from '../components/Common/chart/SQCPieChart';
import { SQCBarChart } from './Common/chart/SQCBarChart';
import {
  alphaNumericSorter,
  getTimeZone,
  returnLocationsAssigned,
  buildFlagKpi,
  includeSummaryInDashboard
} from '../util/util';
import { getCurrentFeatureFlags } from '../util/firebase';
import moment from 'moment';
import Loading from '../components/Common/Loading';
import { Button } from 'react-bootstrap';
import { getFunctions, httpsCallable } from 'firebase/functions';
import firebaseApp from '../util/firebase';
import { useQueryClient } from 'react-query';
import { getConfiguration } from '../util/config';

function DashboardSection({ bg, textColor, bgImage, bgImageOpacity }) {
  const auth = useAuth();
  const router = useRouter();
  const functions = getFunctions(firebaseApp);

  const { data: rawSummariesData } = useGetSummaries();
  const rawSummaries = useMemo(() => {
    return Array.isArray(rawSummariesData) ? rawSummariesData : [];
  }, [rawSummariesData]);

  const { data: companyTypes } = useGetCompanyType();
  const { data: setupGroups } = useGetSetupGroups();
  const { data: rawRvpchecks } = useGetRvpCheckData();

  const [currentSummaries, setCurrentSummaries] = useState([]);
  const [currentSummariesWithEverFlag, setCurrentSummariesWithEverFlag] = useState([]);
  const [currentSummariesTpi, setCurrentSummariesTpi] = useState([]);
  const [allSummaries, setAllSummaries] = useState([]);
  const [indexes, setIndexes] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [currentIndexes, setCurrentIndexes] = useState([]);
  const [allDataComplete, setAllDataComplete] = useState(false);
  const [currentLocation, setCurrentLocation] = useState('');
  const [statusPieChartData, setStatusPieChartData] = useState([]);
  const [tpiPieChartData, setTpiPieChartData] = useState([]);
  const [warningPieChartData, setWarningPieChartData] = useState([]);
  const [oocPieChartData, setOocPieChartData] = useState([]);
  const [currentSummariesOOC, setCurrentSummariesOOC] = useState([]);
  const [currentSummariesWarning, setCurrentSummariesWarning] = useState([]);
  const [currentRvpLocationSelected, setCurrentRvpLocationSelected] = useState(null);
  const featureFlags = getCurrentFeatureFlags();
  const [tz] = useState(getTimeZone(featureFlags));
  const dateFormat = 'MM/DD/YYYY HH:mm';
  const [turnOnRVPcheck, setTurnOnRVPcheck] = useState(true);
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [validFullKeys, setValidFullKeys] = useState(new Set());
  const isNonUs = companyTypes && companyTypes[0] && companyTypes[0]['nonUs'];
  const useInstrument = featureFlags.includes('use-instrument-based-kpis');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const config = getConfiguration(window.location.hostname);
  const sqcChartDetailsColumnAdjustments = config?.sqcChartDetailsColumnAdjustments;
  const shouldLoadData = isNonUs && useInstrument;

  const recalculateChartStagesFunc = httpsCallable(functions, 'recalculateChartStages');
  const [isRecalculating, setIsRecalculating] = useState(false);

  // Fetch only silenced and omitted data points (instead of all 20k+ documents)
  const { data: silencedData = [] } = useGetAllData(shouldLoadData);
  const { data: omittedData = [] } = useGetOmittedData(shouldLoadData);
  const { data: notIncludeOmittedData = [] } = useGetNotIncludeOmittedData(
    sqcChartDetailsColumnAdjustments
  );

  // Combine and deduplicate the datasets
  const allDataPoints = useMemo(() => {
    if (!shouldLoadData) return [];

    const combined = [...silencedData, ...omittedData];
    const uniqueMap = new Map();

    combined.forEach((item) => {
      if (!item.fullKey) return;

      if (!uniqueMap.has(item.fullKey)) {
        uniqueMap.set(item.fullKey, { ...item });
      } else {
        const existing = uniqueMap.get(item.fullKey);

        const combinedEverFlags = Array.from(
          new Set([...(existing.everFlags || []), ...(item.everFlags || [])])
        );

        uniqueMap.set(item.fullKey, {
          ...existing,
          everFlags: combinedEverFlags
        });
      }
    });

    return Array.from(uniqueMap.values());
  }, [silencedData, omittedData, shouldLoadData]);
  const allRvpCheckData = useMemo(() => {
    if (!Array.isArray(rawRvpchecks)) return [];

    return rawRvpchecks.map((rvpCheck) => ({
      ...rvpCheck,
      difference: parseFloat(rvpCheck.difference).toFixed(2),
      expectedResult: parseFloat(rvpCheck.expectedResult).toFixed(2),
      terminalResult: parseFloat(rvpCheck.terminalResult).toFixed(2),
      dateTime: moment(rvpCheck.dateTime)
        .tz(tz || 'UTC')
        .format(dateFormat)
    }));
  }, [rawRvpchecks, tz]);

  const currentRvpCheckData = useMemo(() => {
    let filteredData = allRvpCheckData;

    if (currentRvpLocationSelected) {
      filteredData = filteredData.filter((rvp) => rvp.location === currentRvpLocationSelected);
    }

    if (startDate && endDate) {
      const startMoment = moment(startDate).startOf('day');
      const endMoment = moment(endDate).endOf('day');

      filteredData = filteredData.filter((rvp) => {
        const rvpDate = moment(rvp.dateTime, dateFormat);
        return rvpDate.isBetween(startMoment, endMoment, null, '[]');
      });
    }

    return filteredData;
  }, [allRvpCheckData, currentRvpLocationSelected, startDate, endDate, dateFormat]);

  const currentRvpCheckLocations = useMemo(() => {
    if (allRvpCheckData.length === 0) {
      setTurnOnRVPcheck(false);
      return [];
    } else {
      setTurnOnRVPcheck(true);
      const locations = [...new Set(allRvpCheckData.map((rvp) => rvp.location).filter(Boolean))];
      return locations.map((location) => ({ name: location })).sort(alphaNumericSorter);
    }
  }, [allRvpCheckData]);

  const numberComparator = (v1, v2) => {
    const d1 = Number(v1);
    const d2 = Number(v2);
    return d1 - d2;
  };

  const rvpCheckDataColumns = [
    {
      headerName: 'Date',
      field: 'dateTime',
      flex: 1,
      minWidth: 100,
      sortComparator: (date1, date2) => moment(date1, dateFormat).diff(moment(date2, dateFormat))
    },
    { headerName: 'Sample Id', field: 'sampleId', flex: 1, minWidth: 100 },
    { headerName: 'Location', field: 'location', flex: 1, minWidth: 100 },
    {
      headerName: 'Expected Result',
      field: 'expectedResult',
      flex: 1,
      minWidth: 100,
      sortComparator: numberComparator
    },
    {
      headerName: 'Terminal Result',
      field: 'terminalResult',
      flex: 1,
      minWidth: 100,
      sortComparator: numberComparator
    },
    {
      headerName: 'Difference',
      field: 'difference',
      flex: 1,
      minWidth: 100,
      sortComparator: numberComparator
    }
  ];

  const handleLocationRVPChange = (e) => {
    setCurrentRvpLocationSelected(e.target.value || null);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const updateStatusPieChart = (pieSummaries) => {
    const updatedChartStatuses = [];
    pieSummaries.forEach((summary) => {
      updatedChartStatuses.push(summary.chartStatus);
    });
    const uniqueChartStatuses = uniq(updatedChartStatuses);
    const mappedChartStatuses = [];
    uniqueChartStatuses.forEach((uniqueChartStatus) => {
      const mappedChartStatus = {
        name: uniqueChartStatus,
        value: updatedChartStatuses.filter((chartStatus) => chartStatus === uniqueChartStatus)
          .length
      };
      mappedChartStatuses.push(mappedChartStatus);
    });
    setStatusPieChartData(mappedChartStatuses);
  };

  // OOC / Warning KPIs are driven by CURRENT flags (summary.ooc_or / warnings_or),
  // which the server collects only from non-silenced points — i.e. active flags in
  // the current window. Grouped by instrument (non-US) or method (US). This replaces
  // the earlier instrument-only path that counted everFlags of silenced/omitted
  // points, which made in-control charts show as OOC/Warning (Arteco/Thijs).
  const handleLocationOOCChange = useCallback(
    (eOrLocation) => {
      const selectedLocation =
        typeof eOrLocation === 'string' ? eOrLocation : eOrLocation?.target?.value || '';
      const { rows, counts } = buildFlagKpi(allSummaries, selectedLocation, isNonUs, 'ooc');
      setCurrentSummariesOOC(rows);
      const rawData = Array.from(counts, ([name, value]) => ({ name, value }));
      const total = rawData.reduce((sum, d) => sum + d.value, 0);
      const percentData = rawData.map((d) => ({
        ...d,
        value: total > 0 ? (d.value / total) * 100 : 0
      }));
      setOocPieChartData(percentData);
    },
    [allSummaries, isNonUs]
  );

  const handleLocationWarningChange = useCallback(
    (eOrLocation) => {
      const selectedLocation =
        typeof eOrLocation === 'string' ? eOrLocation : eOrLocation?.target?.value || '';
      const { rows, counts } = buildFlagKpi(allSummaries, selectedLocation, isNonUs, 'warning');
      setCurrentSummariesWarning(rows);
      const rawData = Array.from(counts, ([name, value]) => ({ name, value }));
      const total = rawData.reduce((sum, d) => sum + d.value, 0);
      const percentData = rawData.map((d) => ({
        ...d,
        value: total > 0 ? (d.value / total) * 100 : 0
      }));
      setWarningPieChartData(percentData);
    },
    [allSummaries, isNonUs]
  );

  useEffect(() => {
    if (
      allSummaries?.length > 0 &&
      allDataPoints?.length > 0 &&
      isNonUs !== undefined &&
      useInstrument !== undefined
    ) {
      handleLocationOOCChange('');
      handleLocationWarningChange('');
    }
  }, [
    allSummaries,
    allDataPoints,
    isNonUs,
    useInstrument,
    handleLocationOOCChange,
    handleLocationWarningChange
  ]);
  const renderOptionsDynamic = (dropdownOptions) => {
    const rows = [];
    dropdownOptions.forEach((option) => {
      rows.push(<option value={option.name}>{option.name}</option>);
    });
    return rows;
  };

  // Function to recursively extract all valid fullKeys from the non-archive index
  const extractFullKeysFromIndex = (indexData) => {
    const fullKeys = new Set();

    if (!indexData || !indexData.index || !Array.isArray(indexData.index)) {
      return fullKeys;
    }

    // Recursive function to build fullKeys from the hierarchical structure
    const buildKeys = (items, parentPath = '', level = 0) => {
      const levelNames = ['location', 'method', 'sampleName', 'instrument', 'parameter'];

      items.forEach((item) => {
        const currentPath = parentPath ? `${parentPath}|${item.name}` : item.name;

        // If we're at the parameter level (last level), we have a complete fullKey
        if (level === 4) {
          fullKeys.add(currentPath);
        } else {
          // Recursively process the next level
          const nextLevelKey = levelNames[level + 1];
          if (item[nextLevelKey] && Array.isArray(item[nextLevelKey])) {
            buildKeys(item[nextLevelKey], currentPath, level + 1);
          }
        }
      });
    };

    buildKeys(indexData.index);
    return fullKeys;
  };

  useEffect(() => {
    if (Array.isArray(indexes) && indexes.length > 0) {
      setCurrentIndexes(indexes);
      const nonArchiveIndex = indexes.find((index) => index.id === 'mainIndex-location');
      if (nonArchiveIndex) {
        const extractedKeys = extractFullKeysFromIndex(nonArchiveIndex);
        setValidFullKeys(extractedKeys);
      }
    }
  }, [indexes]);

  useEffect(() => {
    (async () => {
      const result = await getIndexValues();
      setIndexes(result.data);
    })();
  }, []);

  useEffect(() => {
    if (
      Array.isArray(currentIndexes) &&
      auth &&
      Array.isArray(setupGroups) &&
      currentIndexes.length > 0 &&
      !allDataComplete
    ) {
      const activeIndexes = currentIndexes.filter((index) => !index.id.includes('Archive'));
      const locationsAssigned = returnLocationsAssigned(activeIndexes[0].index, setupGroups, auth);
      setAllLocations([...locationsAssigned]);
      setAllDataComplete(true);
    }
  }, [currentIndexes, auth, setupGroups]);

  const aggregateFlags = (summaryIn) => {
    const summary = summaryIn;
    const oocLength = summary.ooc_or?.length || 0;
    const warningsLength = summary.warnings_or?.length || 0;

    if (warningsLength > 0 && oocLength > 0) {
      summary.flags = `OOC: ${summary.ooc_or}, Warning: ${summary.warnings_or}`;
      summary.chartStatus = 'OOC';
    } else if (oocLength > 0) {
      summary.flags = `OOC: ${summary.ooc_or}`;
      summary.chartStatus = 'OOC';
    } else if (warningsLength > 0) {
      summary.flags = `Warning: ${summary.warnings_or}`;
      summary.chartStatus = 'Warning';
    } else {
      summary.flags = '';
      summary.chartStatus = 'In control';
    }
    return summary;
  };

  useEffect(() => {
    const summaries = Array.isArray(rawSummaries)
      ? rawSummaries.filter((summary) => includeSummaryInDashboard(summary.id))
      : [];
    setAllSummaries(summaries);
  }, [rawSummaries]);
  const queryClient = useQueryClient();

  const handleRefresh = async () => {
    if (isRefreshing || isRecalculating) return;

    try {
      setAllDataComplete(false);
      setIsRefreshing(true);
      setIsRecalculating(true);

      const indexResult = await getIndexValues();
      setIndexes(indexResult?.data || []);

      const activeIndexes = (indexResult?.data || []).filter(
        (index) => !index.id.includes('Archive')
      );

      const updatedLocations = [];
      for (const active of activeIndexes) {
        const locations = returnLocationsAssigned(active.index, setupGroups || [], auth);
        updatedLocations.push(...locations);
      }

      const uniqueLocations = [
        ...new Map(updatedLocations.map((loc) => [loc.id || loc.name, loc])).values()
      ];

      setAllLocations(uniqueLocations);
      const filteredSummaries = rawSummaries.filter((s) => includeSummaryInDashboard(s.id));

      setAllSummaries(filteredSummaries);

      await recalculateChartStagesFunc({
        summariesData: filteredSummaries,
        variation: ''
      });

      await queryClient.invalidateQueries(['summary']);

      setCurrentLocation('');
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsRecalculating(false);
      setIsRefreshing(false);
      setAllDataComplete(true);
    }
  };
  useEffect(() => {
    if (!Array.isArray(rawSummaries) || rawSummaries.length === 0) return;

    const assignedLocationsSet = new Set(allLocations.map((loc) => loc.name || loc.id));
    const summaries = rawSummaries.filter((s) => {
      if (!includeSummaryInDashboard(s.id)) return false;

      // Only filter by assigned locations if allLocations is populated
      if (allLocations.length > 0) {
        const location = s.id.substring(0, s.id.indexOf('|'));
        return assignedLocationsSet.has(location);
      }

      return true;
    });

    const hasIdSummaries =
      validFullKeys.size > 0 ? summaries.filter((s) => validFullKeys.has(s.id)) : summaries;
    const filteredSummaries = hasIdSummaries.filter((s) => includeSummaryInDashboard(s.id));
    if (filteredSummaries.length === 0) return;
    if (filteredSummaries.length > 0) {
      const chartStatuses = [];
      const tpiValues = [];
      for (const summary of filteredSummaries) {
        summary.recent_sample = parseFloat(summary.recent_sample).toFixed(2);
        summary.tpi_or = parseFloat(summary.tpi_or).toFixed(2);
        summary.sp_or = parseFloat(summary.sp_or).toFixed(2);
        summary.avg_or = parseFloat(summary.avg_or).toFixed(2);
        if (isFinite(parseFloat(summary.percent_rsd))) {
          summary.percent_rsd = parseFloat(summary.percent_rsd).toFixed(2);
        }
        if (!summary.hasOwnProperty('chartStage') || summary.chartStage === '') {
          summary.chartStage = 'In Development';
        } else {
          summary.chartStage = summary.chartStage;
        }
        aggregateFlags(summary);
        chartStatuses.push(summary.chartStatus);
        tpiValues.push(summary.tpi_or);
        const [location, method, sampleName, instrument, parameter] = summary.id.split('|');

        summary.location = location;
        summary.method = method;
        summary.sampleName = sampleName;
        summary.instrument = instrument;
        summary.parameter = parameter;
      }
      const uniqueChartStatuses = uniq(chartStatuses);
      const mappedChartStatuses = [];
      uniqueChartStatuses.forEach((uniqueChartStatus) => {
        const mappedChartStatus = {
          name: uniqueChartStatus,
          value: chartStatuses.filter((chartStatus) => chartStatus === uniqueChartStatus).length
        };
        mappedChartStatuses.push(mappedChartStatus);
      });
      const mappedTpiValues = [
        {
          name: '0.0 - 0.6',
          value: 0
        },
        {
          name: '0.61 - 1.0',
          value: 0
        },
        {
          name: '1.01 - 1.2',
          value: 0
        },
        {
          name: '1.21 - 2.0',
          value: 0
        },
        {
          name: '> 2.0',
          value: 0
        }
      ];
      tpiValues.forEach((value) => {
        const currentTpiValue = Number(value);
        if (currentTpiValue <= 0.6 && currentTpiValue > 0) {
          const indexToIncrement = mappedTpiValues.findIndex(
            (tpiObject) => tpiObject.name === '0.0 - 0.6'
          );
          mappedTpiValues[indexToIncrement].value += 1;
        }
        if (currentTpiValue > 0.6 && currentTpiValue <= 1) {
          const indexToIncrement = mappedTpiValues.findIndex(
            (tpiObject) => tpiObject.name === '0.61 - 1.0'
          );
          mappedTpiValues[indexToIncrement].value += 1;
        }
        if (currentTpiValue > 1.01 && currentTpiValue < 1.21) {
          const indexToIncrement = mappedTpiValues.findIndex(
            (tpiObject) => tpiObject.name === '1.01 - 1.2'
          );
          mappedTpiValues[indexToIncrement].value += 1;
        }
        if (currentTpiValue >= 1.21 && currentTpiValue <= 2.0) {
          const indexToIncrement = mappedTpiValues.findIndex(
            (tpiObject) => tpiObject.name === '1.21 - 2.0'
          );
          mappedTpiValues[indexToIncrement].value += 1;
        }
        if (currentTpiValue > 2.0) {
          const indexToIncrement = mappedTpiValues.findIndex(
            (tpiObject) => tpiObject.name === '> 2.0'
          );
          mappedTpiValues[indexToIncrement].value += 1;
        }
      });

      if (useInstrument && Array.isArray(allDataPoints)) {
        const filteredForFlags = allDataPoints.filter(
          (item) =>
            item.silence?.toString().toUpperCase() === 'Y' ||
            item.omitFromSqc?.toString().toUpperCase() === 'Y'
        );
        const everFlagsMap = new Map(
          filteredForFlags.map((item) => [item.fullKey, item.everFlags])
        );

        filteredSummaries.forEach((summary) => {
          summary.everFlags = everFlagsMap.get(summary.id) || [];
        });
      }
      setCurrentSummaries(filteredSummaries);
      setCurrentSummariesTpi(filteredSummaries);
      setAllSummaries(filteredSummaries);
      const oocKpi = buildFlagKpi(filteredSummaries, '', isNonUs, 'ooc');
      const warningKpi = buildFlagKpi(filteredSummaries, '', isNonUs, 'warning');
      setCurrentSummariesWarning(warningKpi.rows);
      setCurrentSummariesOOC(oocKpi.rows);
      setStatusPieChartData(mappedChartStatuses);
      setTpiPieChartData(mappedTpiValues);
      setOocPieChartData(Array.from(oocKpi.counts, ([name, value]) => ({ name, value })));
      setWarningPieChartData(Array.from(warningKpi.counts, ([name, value]) => ({ name, value })));
    }
  }, [rawSummaries, validFullKeys, allLocations]);

  const notIncludeOmittedDataMap = useMemo(() => {
    const map = new Map();

    notIncludeOmittedData?.forEach((d) => {
      const key = d.fullKey;
      const hasEverFlags = d.everFlags && d.everFlags.length > 0;

      if (!map.has(key)) {
        map.set(key, 0);
      }
      if (hasEverFlags) {
        map.set(key, map.get(key) + 1);
      }
    });
    return map;
  }, [notIncludeOmittedData]);

  useEffect(() => {
    if (sqcChartDetailsColumnAdjustments) {
      if (currentSummaries?.length > 0 && silencedData?.length > 0) {
        const summaryWithEverFlags = currentSummaries.map((s) => {
          const everFlagCount = notIncludeOmittedDataMap.get(s.id) || '';
          return { ...s, everFlagCount };
        });
        setCurrentSummariesWithEverFlag([...summaryWithEverFlags]);
      } else {
        setCurrentSummariesWithEverFlag([...currentSummaries]);
      }
    } else {
      setCurrentSummariesWithEverFlag([...currentSummaries]);
    }
  }, [currentSummaries, silencedData, sqcChartDetailsColumnAdjustments]);

  if (!allDataComplete) {
    return (
      <Section
        bg={bg}
        textColor={textColor}
        size="md"
        bgImage={bgImage}
        bgImageOpacity={bgImageOpacity}
      >
        <Loading />
      </Section>
    );
  }

  const normalizeEverFlagText = (flagItem) => {
    if (!flagItem) return '';
    if (typeof flagItem === 'string') return flagItem;
    if (typeof flagItem === 'object') {
      if (flagItem.flags) return flagItem.flags;
      if (flagItem.version && flagItem.flags) return flagItem.flags;
    }
    return '';
  };

  const controlStatusColumns = [
    { field: 'location', headerName: 'Location', flex: 3, minWidth: 200 },
    { field: 'methodAlias', headerName: 'Alias', flex: 1, minWidth: 100 },
    { field: 'method', headerName: 'Method', flex: 1, minWidth: 100 },
    { field: 'parameter', headerName: 'Parameter', flex: 2, minWidth: 150 },
    { field: 'flags', headerName: 'Flag', flex: 3, minWidth: 200 }
  ];

  const tpiColumns = [
    { headerName: 'Location', field: 'location', flex: 3, minWidth: 200 },
    { headerName: 'Alias', field: 'methodAlias', flex: 1, minWidth: 100 },
    { headerName: 'Method', field: 'method', flex: 1, minWidth: 100 },
    { headerName: 'Parameter', field: 'parameter', flex: 2, minWidth: 200 },
    {
      headerName: 'TPI',
      field: 'tpi_or',
      flex: 1,
      minWidth: 100,
      valueFormatter: (param) => {
        if (Number(param.value) === 0 || Number.isNaN(Number(param.value))) return '';
        return param.value;
      }
    }
  ];
  const oocColumns = isNonUs
    ? [
        { headerName: 'Location', field: 'location', flex: 3, minWidth: 200 },
        { headerName: 'Alias', field: 'methodAlias', flex: 1, minWidth: 100 },
        { headerName: 'Instrument', field: 'instrument', flex: 1, minWidth: 100 },
        { headerName: 'Method', field: 'method', flex: 1, minWidth: 100 },
        { headerName: 'Parameter', field: 'parameter', flex: 2, minWidth: 200 },
        useInstrument
          ? {
              headerName: 'Out of Control',
              field: 'everFlags',
              flex: 1,
              minWidth: 200,
              valueGetter: (params) => {
                const flags = params.row?.everFlags || [];
                const results = [];

                for (const f of flags) {
                  const flagString = normalizeEverFlagText(f);

                  const match = flagString.match(/ooc\s*:\s*(.*)/i);
                  if (match && match[1]) {
                    const values = match[1]
                      .split(',')
                      .map((v) => v.trim())
                      .filter(Boolean);

                    results.push(...values);
                  }
                }

                return [...new Set(results)];
              }
            }
          : {
              headerName: 'Out of Control',
              field: 'ooc_or',
              flex: 1,
              minWidth: 200
            }
      ]
    : [
        { headerName: 'Location', field: 'location', flex: 3, minWidth: 200 },
        { headerName: 'Alias', field: 'methodAlias', flex: 1, minWidth: 100 },
        { headerName: 'Method', field: 'method', flex: 1, minWidth: 100 },
        { headerName: 'Parameter', field: 'parameter', flex: 2, minWidth: 200 },
        {
          headerName: 'Out of Control',
          field: 'ooc_or',
          flex: 1,
          minWidth: 200
        }
      ];

  const warningColumns = isNonUs
    ? [
        { headerName: 'Location', field: 'location', flex: 3, minWidth: 200 },
        { headerName: 'Alias', field: 'methodAlias', flex: 1, minWidth: 100 },
        { headerName: 'Instrument', field: 'instrument', flex: 1, minWidth: 100 },
        { headerName: 'Method', field: 'method', flex: 1, minWidth: 100 },
        { headerName: 'Parameter', field: 'parameter', flex: 2, minWidth: 200 },
        useInstrument
          ? {
              headerName: 'Warning',
              field: 'everFlags',
              flex: 1,
              minWidth: 200,
              valueGetter: (params) => {
                const flags = params.row?.everFlags || [];
                const results = [];

                for (const f of flags) {
                  const flagString = normalizeEverFlagText(f);

                  const match = flagString.match(/\b(?:w|warning|warnings)\b\s*:\s*(.*)/i);
                  if (match?.[1]) {
                    const values = match[1]
                      .split(',')
                      .map((v) => v.trim())
                      .filter(Boolean);

                    results.push(...values);
                  }
                }

                return Array.from(new Set(results)).join(', ');
              }
            }
          : {
              headerName: 'Warning',
              field: 'warnings_or',
              flex: 1,
              minWidth: 200
            }
      ]
    : [
        { headerName: 'Location', field: 'location', flex: 3, minWidth: 200 },
        { headerName: 'Alias', field: 'methodAlias', flex: 1, minWidth: 100 },
        { headerName: 'Method', field: 'method', flex: 1, minWidth: 100 },
        { headerName: 'Parameter', field: 'parameter', flex: 2, minWidth: 200 },
        {
          headerName: 'Warning',
          field: 'warnings_or',
          flex: 1,
          minWidth: 200
        }
      ];

  const detailsColumns = [
    { headerName: 'Location', field: 'location', flex: 3, minWidth: 250 },
    ...(!sqcChartDetailsColumnAdjustments
      ? [{ headerName: 'Alias', field: 'methodAlias', flex: 1, minWidth: 80 }]
      : []),
    { headerName: 'Method', field: 'method', flex: 1, minWidth: 80 },
    { headerName: 'Instrument', field: 'instrument', flex: 1, minWidth: 100 },
    { headerName: 'Sample Name', field: 'sampleName', flex: 1.5, minWidth: 150 },
    { headerName: 'Parameters', field: 'parameter', flex: 1.5, minWidth: 150 },
    { headerName: 'Chart stage', field: 'chartStage', flex: 1.5, minWidth: 150 },
    { headerName: 'Chart status', field: 'chartStatus', flex: 1, minWidth: 80 },
    { headerName: 'Out of control', field: 'ooc_or', flex: 1, minWidth: 81 },
    { headerName: 'Warnings', field: 'warnings_or', flex: 1, minWidth: 100 },
    { headerName: 'Most recent sample', field: 'recent_date', flex: 2, minWidth: 150 },
    {
      headerName: 'Result',
      field: 'recent_sample',
      flex: 1,
      minWidth: 80,
      valueFormatter: (param) => {
        if (Number(param.value) === 0 || Number.isNaN(Number(param.value))) return '';
        return param.value;
      }
    },
    {
      headerName: 'Average',
      field: 'avg_or',
      flex: 1,
      minWidth: 100,
      valueFormatter: (param) => {
        if (Number(param.value) === 0 || Number.isNaN(Number(param.value))) return '';
        return param.value;
      }
    },
    {
      headerName: '% RSD',
      field: 'percent_rsd',
      flex: 1,
      minWidth: 100,
      valueFormatter: (param) => {
        if (Number(param.value) === 0 || Number.isNaN(Number(param.value))) return '';
        return param.value;
      }
    },
    {
      headerName: 'Site Precision (R’)',
      field: 'sp_or',
      flex: 1,
      minWidth: 100,
      valueFormatter: (param) => {
        if (Number(param.value) === 0 || Number.isNaN(Number(param.value))) return '';
        return param.value;
      }
    },
    {
      headerName: 'TPI',
      field: 'tpi_or',
      flex: 1,
      minWidth: 50,
      valueFormatter: (param) => {
        if (Number(param.value) === 0 || Number.isNaN(Number(param.value))) return '';
        return param.value;
      }
    },
    ...(sqcChartDetailsColumnAdjustments
      ? [{ headerName: 'EverFlags', field: 'everFlagCount', flex: 1, minWidth: 80 }]
      : []),
    ...(sqcChartDetailsColumnAdjustments
      ? [{ headerName: 'n', field: 'n_or', flex: 1, minWidth: 80 }]
      : [])
  ];

  const COLORS = ['#FF2035', '#FF8042', '#FFBB28', '#00C49F', '#0088FE'];
  const statusColors = ['#45c425', '#FF2035', '#ffd700'];

  const options = {
    // pageStartIndex: 0,
    sizePerPage: 10,
    hideSizePerPage: true,
    hidePageListOnlyOnePage: true
  };

  const kpiOptions = {
    // pageStartIndex: 0,
    sizePerPage: 5,
    hideSizePerPage: true,
    hidePageListOnlyOnePage: true
  };

  const renderCellWithColor = (entry) => {
    const cellToReturn = [];
    if (entry.name === 'In control') {
      cellToReturn.push(<Cell fill={statusColors[0]} />);
    } else if (entry.name === 'OOC') {
      cellToReturn.push(<Cell fill={statusColors[1]} />);
    } else if (entry.name === 'Warning') {
      cellToReturn.push(<Cell fill={statusColors[2]} />);
    }
    return cellToReturn;
  };

  const handleGlobalLocationChange = (e) => {
    const location = e.target.value;
    setCurrentLocation(location);
    const filteredSummaries =
      location !== ''
        ? allSummaries.filter((summary) => summary.location === location)
        : allSummaries;

    // Update status chart
    updateStatusPieChart(filteredSummaries);
    setCurrentSummaries(filteredSummaries);

    // Update TPI chart
    const tpiRanges = [
      { name: '0.0 - 0.6', min: 0, max: 0.6 },
      { name: '0.61 - 1.0', min: 0.6, max: 1.0 },
      { name: '1.01 - 1.2', min: 1.01, max: 1.2 },
      { name: '1.21 - 2.0', min: 1.21, max: 2.0 },
      { name: '> 2.0', min: 2.0, max: Infinity }
    ];

    const mappedTpiValues = tpiRanges.map((range) => ({
      name: range.name,
      value: filteredSummaries.filter((s) => {
        const tpi = parseFloat(s.tpi_or);
        if (isNaN(tpi) || tpi <= 0) return false;

        // Match original boundary logic
        if (range.name === '0.0 - 0.6') return tpi > 0 && tpi <= 0.6;
        if (range.name === '0.61 - 1.0') return tpi > 0.6 && tpi <= 1.0;
        if (range.name === '1.01 - 1.2') return tpi > 1.01 && tpi < 1.21;
        if (range.name === '1.21 - 2.0') return tpi >= 1.21 && tpi <= 2.0;
        if (range.name === '> 2.0') return tpi > 2.0;
        return false;
      }).length
    }));
    setCurrentSummariesTpi(filteredSummaries);
    setTpiPieChartData(mappedTpiValues);

    handleLocationOOCChange({ target: { value: location } });
    handleLocationWarningChange({ target: { value: location } });
  };

  return (
    <Section
      bg={bg}
      textColor={textColor}
      size="md"
      bgImage={bgImage}
      bgImageOpacity={bgImageOpacity}
    >
      {router.query.paid && auth.user.planIsActive && (
        <Alert variant="success" className="text-center mx-auto mb-5" style={{ maxWidth: '400px' }}>
          You are now subscribed to the {auth.user.planId} plan
          <span className="ml-2" role="img" aria-label="party">
            🥳
          </span>
        </Alert>
      )}

      <Row>
        <Col lg={12}>
          <div className="d-flex justify-content-end ">
            <Button
              variant="primary"
              size="sm"
              className="px-4 py-2 d-flex align-items-center gap-2"
              onClick={handleRefresh}
              disabled={isRefreshing || isRecalculating}
            >
              {isRefreshing || isRecalculating ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  Refreshing...
                </>
              ) : (
                <>Refresh</>
              )}
            </Button>
          </div>
          {isRefreshing || isRecalculating ? (
            <Loading />
          ) : (
            <Tabs defaultActiveKey="kpis" id="uncontrolled-tab-example" className="tri-tabs">
              <Tab eventKey="kpis" title="KPIs">
                <div className="full-kpi-container">
                  <div className="left-kpi-container">
                    <h3 className="kpi-header">Key Performance Indices - Control Status</h3>
                    <Card className="card-class">
                      <Card.Body>
                        <div className="chart-title">
                          <h3>Control Status</h3>
                        </div>
                        <div className="chart-container">
                          <PieChart width={330} height={320}>
                            <Pie
                              data={statusPieChartData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={130}
                              fill="#8884d8"
                            >
                              {statusPieChartData.map((entry) => renderCellWithColor(entry))}
                            </Pie>
                            <Legend verticalAlign="bottom" height={36} />
                          </PieChart>
                        </div>
                      </Card.Body>
                    </Card>
                    <h3 className="kpi-header">
                      Key Performance Indices - TPI by method and parameter
                    </h3>
                    <Card className="card-class">
                      <Card.Body>
                        <div className="chart-title">
                          <h3>Test Performance Index</h3>
                        </div>
                        <div className="chart-container">
                          <PieChart width={330} height={320}>
                            <Pie
                              data={tpiPieChartData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={130}
                              fill="#8884d8"
                            >
                              {tpiPieChartData.map((entry, index) => (
                                <Cell fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Legend verticalAlign="bottom" height={36} />
                          </PieChart>
                        </div>
                      </Card.Body>
                    </Card>
                    <h3 className="kpi-header">
                      Out of control data by {isNonUs ? 'Instrument' : 'Method'} (
                      {oocPieChartData.length})
                    </h3>
                    <Card className="card-class">
                      <Card.Body>
                        <div className="chart-title">
                          <h3>Out of Control Index</h3>
                        </div>
                        <div className="chart-container">
                          {isNonUs ? (
                            <SQCBarChart data={oocPieChartData} />
                          ) : (
                            <SQCPieChart data={oocPieChartData} />
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                    <h3 className="kpi-header">
                      Warning data by {isNonUs ? 'Instrument' : 'Method'} (
                      {warningPieChartData.length})
                    </h3>
                    <Card className="card-class">
                      <Card.Body>
                        <div className="chart-title">
                          <h3>Warning Index</h3>
                        </div>
                        <div className="chart-container">
                          {isNonUs ? (
                            <SQCBarChart data={warningPieChartData} />
                          ) : (
                            <SQCPieChart data={warningPieChartData} />
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                  <div className="right-kpi-container">
                    <div className="sticky-select">
                      <Form.Group as={Col} controlId="global-location">
                        <Form.Select
                          value={currentLocation}
                          onChange={handleGlobalLocationChange}
                          aria-label="Select location"
                        >
                          <option value="">All Locations</option>
                          {renderOptionsDynamic(allLocations)}
                        </Form.Select>
                      </Form.Group>
                    </div>

                    <div className="kpi-table-container">
                      <CustomTable
                        header={controlStatusColumns}
                        data={currentSummaries}
                        numberOfRows={5}
                      />
                    </div>

                    <div className="kpi-table-container">
                      <CustomTable
                        numberOfRows={5}
                        header={tpiColumns}
                        data={currentSummariesTpi}
                      />
                    </div>

                    <div className="kpi-ooc-warning-table-container">
                      <CustomTable
                        numberOfRows={5}
                        header={oocColumns}
                        data={currentSummariesOOC}
                      />
                    </div>

                    <div className="kpi-ooc-warning-table-container">
                      <CustomTable
                        numberOfRows={5}
                        header={warningColumns}
                        data={currentSummariesWarning}
                      />
                    </div>
                  </div>
                </div>
                {allRvpCheckData.length > 0 && (
                  <div>
                    <div className="rvp-check-container">
                      <h3 className="kpi-header">RVP check</h3>
                      <div className="rvp-filters">
                        <Form.Group as={Col} controlId="location-rvp">
                          <Form.Select
                            value={currentRvpLocationSelected || ''}
                            onChange={handleLocationRVPChange}
                            aria-label="Default select example"
                          >
                            <option value="">Select location</option>
                            {renderOptionsDynamic(currentRvpCheckLocations)}
                          </Form.Select>
                        </Form.Group>
                        <Form.Group as={Col} controlId="start-date-rvp">
                          <Form.Label>Start Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={startDate}
                            onChange={handleStartDateChange}
                          />
                        </Form.Group>
                        <Form.Group as={Col} controlId="end-date-rvp">
                          <Form.Label>End Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={endDate}
                            onChange={handleEndDateChange}
                          />
                        </Form.Group>
                      </div>
                      <div className="rvp-check-table">
                        <CustomTable
                          numberOfRows={10}
                          header={rvpCheckDataColumns}
                          data={currentRvpCheckData}
                          sort={{ sorting: { sortModel: [{ field: 'dateTime', sort: 'asc' }] } }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </Tab>
              <Tab eventKey="details" title="Details">
                <div className="chart-details-container">
                  <div className="chart-details-header">
                    <div className="header">
                      <h2>Chart Details</h2>
                    </div>
                    <div className="underline-header">
                      <hr />
                    </div>
                  </div>
                  <div className="chart-details-table">
                    <CustomTable
                      header={detailsColumns}
                      data={
                        !sqcChartDetailsColumnAdjustments
                          ? currentSummaries
                          : currentSummariesWithEverFlag
                      }
                      numberOfRows={10}
                    />
                  </div>
                  <div className="flag-definitions">
                    <h3>Flag Definitions</h3>
                    <Card className="flag-definitions-card">
                      <Card.Body>
                        <div className="flag-definitions-container">
                          <div className="left-column">
                            <div className="flag-row">
                              <div className="flag">UCL</div>
                              <div className="description">
                                Individual result above Upper Control Limit
                              </div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">LCL</div>
                              <div className="description">
                                Individual result below Lower Control Limit
                              </div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">
                                RC
                                {'>'}
                                2&sigma;
                              </div>
                              <div className="description">
                                Recheck result above 2&sigma; from center line
                              </div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">
                                RC
                                {'<'}
                                2&sigma;
                              </div>
                              <div className="description">
                                Recheck result below 2&sigma; from center line
                              </div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">
                                2/3
                                {'>'}
                                2&sigma;
                              </div>
                              <div className="description">
                                2/3 of results {'>'} 2&sigma; from center line
                              </div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">
                                2/3
                                {'<'}
                                2&sigma;
                              </div>
                              <div className="description">
                                2/3 of results {'<'} 2&sigma; from center line
                              </div>
                            </div>
                          </div>
                          <div className="center-column">
                            <div className="flag-row">
                              <div className="flag">
                                5CP
                                {'>'}
                                1&sigma;
                              </div>
                              <div className="description">
                                5 consecutive points {'>'} 1&sigma; from the center line
                              </div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">
                                5CP
                                {'<'}
                                1&sigma;
                              </div>
                              <div className="description">
                                5 consecutive points {'<'} 1&sigma; from the center line
                              </div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">7CP-I</div>
                              <div className="description">7 consecutive points increasing</div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">7CP-D</div>
                              <div className="description">7 consecutive points decreasing</div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">
                                9CP
                                {'>'}
                                CL
                              </div>
                              <div className="description">
                                9 consecutive points above the center line
                              </div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">
                                9CP
                                {'<'}
                                CL
                              </div>
                              <div className="description">
                                9 consecutive points below the center line
                              </div>
                            </div>
                          </div>
                          <div className="right-column">
                            <div className="flag-row">
                              <div className="flag">E-UCL</div>
                              <div className="description">EWMA trend line above the trend UCL</div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">E-LCL</div>
                              <div className="description">EWMA trend line below the trend UCL</div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">
                                5/20
                                {'>'}
                                MR
                              </div>
                              <div className="description">
                                5 of 20 points above the Moving Range UCL
                              </div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">
                                1{'>'}
                                MR
                              </div>
                              <div className="description">
                                Single point above the Moving Range UCL
                              </div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">GESD</div>
                              <div className="description">GESD Outlier in the setup data</div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">ALT</div>
                              <div className="description">Alt chart control limits exceeded</div>
                            </div>
                            <div className="flag-row">
                              <div className="flag">TOL</div>
                              <div className="description">Tolerance limits exceeded</div>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                </div>
              </Tab>
            </Tabs>
          )}
        </Col>
      </Row>
    </Section>
  );
}

DashboardSection.propTypes = {
  bg: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  bgImage: PropTypes.string.isRequired,
  bgImageOpacity: PropTypes.number.isRequired
};

export default DashboardSection;
